import isString from 'lodash/isString';
import moment, { DATE_FORMAT } from '@/utils/moment-wrapper';
import { createTimeSeriesFromDate } from '@/common/time-series-picker/time-series-picker.util';
import { i18n_locale } from '@/utils/i18n';
import { MS_IN_TIME_UNITS } from '@/utils/time.constants';

export const convertTimeToMs = (time, timeUnit) => {
    if (isNaN(time) || !isString(timeUnit)) return;

    const timeUnitInMs = MS_IN_TIME_UNITS[timeUnit.toUpperCase()];
    if (!timeUnitInMs) return;

    return time * timeUnitInMs;
};

export const convertMsToTime = (timeInMs, timeUnit) => {
    if (isNaN(timeInMs) || !isString(timeUnit)) return;

    const timeUnitInMs = MS_IN_TIME_UNITS[timeUnit.toUpperCase()];
    if (!timeUnitInMs) return;

    return timeInMs / timeUnitInMs;
};

function getPreviousDateRange (first, count) {
    const start = moment(first, { ignoreTimeZone: true });
    const previousEnd = start.clone().subtract(1, 'days');
    const previousStart = previousEnd.clone().subtract(count - 1, 'days');

    return {
        previousStart: previousStart.format(DATE_FORMAT.iso),
        previousEnd: previousEnd.format(DATE_FORMAT.iso),
    };
}

export const getPreviousTimeSeries = ({ spec }) => {
    const { previousStart, previousEnd } = getPreviousDateRange(spec.first, spec.count);

    const timeSeries = createTimeSeriesFromDate({ start: previousStart, end: previousEnd }, true);
    timeSeries.config.type = 'Custom';

    return timeSeries;
};

let cachedRelativeTimeLocale;
let cachedRelativeTimeFormatter;

function getRelativeTimeFormatter (locale) {
    if (cachedRelativeTimeFormatter && cachedRelativeTimeLocale === locale) {
        return cachedRelativeTimeFormatter;
    }

    cachedRelativeTimeLocale = locale;
    cachedRelativeTimeFormatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' });

    return cachedRelativeTimeFormatter;
}

/**
 * Format a timestamp relative to now (e.g. "2m ago").
 *
 * @param {Date|string|number|null|undefined} timestamp Date instance, ISO string, or millisecond epoch.
 * @param {string} [locale] Defaults to the active i18n locale.
 * @returns {string}
 */
export function formatRelativeTime (timestamp, locale = i18n_locale?.value) {
    if (timestamp == null) return '';

    const ms = timestamp instanceof Date ? timestamp.getTime() : new Date(timestamp).getTime();
    if (Number.isNaN(ms)) return '';

    const formatter = getRelativeTimeFormatter(locale);
    const diffSeconds = (Date.now() - ms) / 1000;

    if (diffSeconds < 60) return formatter.format(0, 'second');
    if (diffSeconds < 3600) return formatter.format(-Math.floor(diffSeconds / 60), 'minute');
    if (diffSeconds < 86400) return formatter.format(-Math.floor(diffSeconds / 3600), 'hour');

    return formatter.format(-Math.floor(diffSeconds / 86400), 'day');
}
